/**
 * Stopwatch 秒表逻辑
 * 基于时间戳 + requestAnimationFrame，切后台也能保持准确
 */

class Stopwatch {
    constructor() {
        this.startTime = 0; // 开始时间戳（performance.now）
        this.elapsedTime = 0; // 已经过的时间(毫秒)
        this.rafId = null;
        this.lastRender = 0; // 上次渲染时间戳，用于节流非毫秒模式
        this.isRunning = false;
        this.showMilliseconds = false;
        this.splits = []; // Split 记录
        this.callbacks = {
            onTick: null,
            onStart: null,
            onPause: null,
            onReset: null,
            onSplit: null
        };

        this._tickRaf = this._tickRaf.bind(this);
    }

    /**
     * 设置是否显示毫秒
     * @param {boolean} show - 是否显示毫秒
     */
    setShowMilliseconds(show) {
        this.showMilliseconds = show;
        this._triggerCallback('onTick', this.getFormattedTime());

        // 更新刷新节奏
        this._restartInterval();
    }

    /**
     * 获取格式化的时间字符串
     * @returns {string|object} - 如果显示毫秒,返回对象;否则返回字符串
     */
    getFormattedTime() {
        // 归一化为整数毫秒，避免浮点误差导致长小数串
        const totalMs = Math.max(0, Math.floor(this.elapsedTime));
        const totalSeconds = Math.floor(totalMs / 1000);
        const milliseconds = totalMs % 1000;

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

        if (this.showMilliseconds) {
            // 返回对象,包含主时间、毫秒小数点+首位、以及后两位
            const ms = String(milliseconds).padStart(3, '0');
            return {
                main: timeStr,
                msPrimary: ms[0],
                msSecondary: ms.substring(1)
            };
        }

        return timeStr;
    }

    /**
     * 开始秒表
     */
    start() {
        if (this.isRunning) return;

        this.isRunning = true;
        this.startTime = performance.now() - this.elapsedTime;
        this.lastRender = 0;
        this._triggerCallback('onStart');
        this._tickRaf();
    }

    /**
     * 暂停秒表
     */
    pause() {
        if (!this.isRunning) return;

        this._syncElapsed();
        this.isRunning = false;
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        this._triggerCallback('onPause');
    }

    /**
     * 重置秒表
     */
    reset() {
        this.pause();
        this.elapsedTime = 0;
        this.startTime = 0;
        this.lastRender = 0;
        this.splits = [];
        this._triggerCallback('onReset');
        this._triggerCallback('onTick', this.getFormattedTime());
    }

    /**
     * 记录 Split 时间
     */
    split() {
        if (!this.isRunning) return;

        const splitTime = this.getFormattedTime();
        const splitNumber = this.splits.length + 1;

        const splitData = {
            number: splitNumber,
            time: splitTime,
            milliseconds: Math.max(0, Math.floor(this.elapsedTime))
        };

        this.splits.push(splitData);
        this._triggerCallback('onSplit', splitData);

        return splitData;
    }

    /**
     * 获取所有 Split 记录
     */
    getSplits() {
        return this.splits;
    }

    /**
     * 获取当前状态
     */
    getState() {
        return {
            elapsedTime: Math.max(0, Math.floor(this.elapsedTime)),
            isRunning: this.isRunning,
            showMilliseconds: this.showMilliseconds,
            formattedTime: this.getFormattedTime(),
            splits: this.splits
        };
    }

    /**
     * 注册回调函数
     * @param {string} event - 事件名称 (onTick, onStart, onPause, onReset, onSplit)
     * @param {Function} callback - 回调函数
     */
    on(event, callback) {
        if (this.callbacks.hasOwnProperty(event)) {
            this.callbacks[event] = callback;
        }
    }

    /**
     * 触发回调函数
     */
    _triggerCallback(event, data) {
        if (this.callbacks[event] && typeof this.callbacks[event] === 'function') {
            this.callbacks[event](data);
        }
    }

    /**
     * 重新启动刷新循环(用于切换毫秒显示时)
     */
    _restartInterval() {
        if (!this.isRunning) return;
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        this.lastRender = 0;
        this._tickRaf();
    }

    /**
     * 同步已用时间
     */
    _syncElapsed() {
        if (this.isRunning || this.startTime) {
            this.elapsedTime = performance.now() - this.startTime;
        }
    }

    /**
     * rAF 驱动的更新循环
     */
    _tickRaf(timestamp) {
        if (!this.isRunning) return;

        const now = timestamp ?? performance.now();
        this.elapsedTime = now - this.startTime;

        const shouldRender = this.showMilliseconds || !this.lastRender || (now - this.lastRender >= 100);
        if (shouldRender) {
            this.lastRender = now;
            this._triggerCallback('onTick', this.getFormattedTime());
        }

        this.rafId = requestAnimationFrame(this._tickRaf);
    }

    /**
     * 销毁定时器
     */
    destroy() {
        this.pause();
        this.callbacks = {};
    }
}

// 导出
export default Stopwatch;
